import { type } from 'i18next-sprintf-postprocessor';
import constants from '../constants';
import { DialogService } from 'aurelia-dialog';
import { Container } from 'aurelia-framework';
import { UniversalEntitySelect } from '../../../../../../../dialog/universal-entity-select';

export default (traitManager, domComponents, config = {}) => {
    const defaultType = domComponents.getType('default');
    const defaultModel = defaultType.model;
    const defaultView = defaultType.view;
    const {
        containerName,
        wrapperName,
        prevName,
        nextName,
        paginationName,
        scrollbarName,
        wrapperSelector,
        prevSelector,
        nextSelector,
        paginationSelector,
        scrollbarSelector,
        containerId,
        slideSelector,
    } = constants;

    // Helper function to save component state to data attributes
    const saveComponentStateToAttributes = (model) => {
        model.get('traits').forEach((trait) => {
            const traitName = trait.get('name');
            const traitValue = model.get(traitName);
            if (traitValue === undefined || traitValue === null) return;
            const dataAttrName = `data-trait-${traitName}`;
            model.addAttributes({ [dataAttrName]: traitValue });
        });
    };

    // Helper function to load component state from data attributes
    const loadComponentStateFromAttributes = (model) => {
        const attributes = model.getAttributes();
        let hasLoadedAny = false;
        for (const attrName in attributes) {
            if (attrName.startsWith('data-trait-')) {
                const traitName = attrName.replace('data-trait-', '');
                const traitValue = attributes[attrName];
                model.set(traitName, traitValue, { initialLoad: true });
                hasLoadedAny = true;
            }
        }

        return hasLoadedAny;
    };

    const dialogService = Container.instance.get(DialogService);

    traitManager.addType('media-bulk-select', {
        noLabel: true,
        createInput({ trait }) {
            const wrapper = document.createElement('div');
            const button = document.createElement('button');
            button.innerText = 'Mehrere Bilder hochladen';
            button.className = 'gjs-btn-prim';
            button.style.cssText =
                'width:100%;margin-top:6px;padding:6px 10px;cursor:pointer;';
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const component = trait.target;
                dialogService
                    .open({
                        viewModel: UniversalEntitySelect,
                        model: {
                            selectModelId: 'media-database/media-item',
                            conditions: { type: { $in: ['image'] } },
                        },
                    })
                    .whenClosed((data) => {
                        const items = data.output;
                        if (!items || items.length === 0) return;

                        const wrapper = component
                            ?.components()
                            .find(
                                (comp) =>
                                    comp.get('type') === constants.wrapperName
                            );

                        // Remove empty slides
                        if (wrapper) {
                            const children = [...wrapper.components().models];

                            children.forEach((child, index) => {
                                if (!child) {
                                    return;
                                }

                                const isEmptyDiv =
                                    child.get('tagName') === 'div' &&
                                    !child.components().length &&
                                    !child.get('content')?.trim();

                                if (isEmptyDiv) {
                                    child.remove();
                                }
                            });
                        }

                        if (wrapper) {
                            items.forEach((item) => {
                                const src =
                                    item?.file?.downloadUrl ||
                                    (item?.file?.previewUrl
                                        ? item.file.previewUrl
                                              .replace('$width', '1500')
                                              .replace('$height', '-')
                                        : null);
                                if (src) {
                                    const slideHtml = `<div class="${
                                        config.classSlide || 'swiper-slide'
                                    }"><img src="${src}" class="${'sio-image slider-img'}"></div>`;
                                    wrapper.components().add(slideHtml);
                                }
                            });
                        }
                    });
            });
            wrapper.appendChild(button);
            return wrapper;
        },
        onEvent() {},
        onUpdate() {},
    });

    domComponents.addType(containerName, {
        model: defaultModel.extend(
            {
                init() {
                    const defaultProps = {
                        name: 'Slider',
                        badgable: false,
                        initialSlide: '0',
                        speed: 300,
                        slidesPerView: 1,
                        spaceBetween: '0',
                        slidesPerGroup: 1,
                        slidesOffsetBefore: '0',
                        slidesOffsetAfter: '0',
                        direction: 'horizontal',
                        effect: 'slide',
                        autoHeight: false,
                        watchOverflow: true,
                        centeredSlides: false,
                        loop: false,
                        pagination: 'none',
                        scrollbar: false,
                        hideNavigation: false,

                        autoplay: false,
                        autoplayDelay: 3000,
                        autoplayStopOnLastSlide: false,
                        autoplayDisableOnInteraction: true,
                        autoplayReverseDirection: false,
                        autoplayWaitForTransition: true,

                        smallSlidesPerView: 1,
                        smallSpaceBetween: '0',
                        smallSlidesPerGroup: 1,
                        smallGridRows: 1,

                        mediumSlidesPerView: 1,
                        mediumSpaceBetween: '0',
                        mediumSlidesPerGroup: 1,
                        mediumGridRows: 1,

                        largeSlidesPerView: 1,
                        largeSpaceBetween: '0',
                        largeSlidesPerGroup: 1,
                        largeGridRows: 1,

                        traits: [
                            {
                                type: 'media-bulk-select',
                                name: 'mediaBulkSelect',
                            },
                            {
                                type: 'number',
                                label: 'Initial Slide',
                                name: 'initialSlide',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Speed',
                                name: 'speed',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Slides Per View',
                                name: 'slidesPerView',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Space Between',
                                name: 'spaceBetween',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Slides Per Group',
                                name: 'slidesPerGroup',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Slides Offset Before',
                                name: 'slidesOffsetBefore',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Slides Offset After',
                                name: 'slidesOffsetAfter',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Autoplay',
                                name: 'autoplay',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Autoplay Delay',
                                name: 'autoplayDelay',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Autoplay stop on Last slide',
                                name: 'autoplayStopOnLastSlide',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Autoplay disable on interaction',
                                name: 'autoplayDisableOnInteraction',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Autoplay reverse direction',
                                name: 'autoplayReverseDirection',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Autoplay wait for transition',
                                name: 'autoplayWaitForTransition',
                                changeProp: 1,
                            },
                            {
                                type: 'select',
                                label: 'Direction',
                                name: 'direction',
                                changeProp: 1,
                                options: ['horizontal', 'vertical'],
                            },
                            {
                                type: 'select',
                                label: 'Transition effect',
                                name: 'effect',
                                changeProp: 1,
                                options: [
                                    'slide',
                                    'fade',
                                    'cube',
                                    'coverflow',
                                    'flip',
                                ],
                            },
                            {
                                type: 'checkbox',
                                label: 'Free Mode',
                                name: 'freeMode',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Auto Height',
                                name: 'autoHeight',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Watch Overflow',
                                name: 'watchOverflow',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Centered Slides',
                                name: 'centeredSlides',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Loop',
                                name: 'loop',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Scrollbar',
                                name: 'scrollbar',
                                changeProp: 1,
                            },
                            {
                                type: 'checkbox',
                                label: 'Hide Navigation',
                                name: 'hideNavigation',
                                changeProp: 1,
                            },
                            {
                                type: 'select',
                                label: 'Pagination',
                                name: 'pagination',
                                changeProp: 1,
                                options: [
                                    'none',
                                    'bullets',
                                    'fraction',
                                    'progressbar',
                                ],
                            },

                            // Small
                            {
                                type: 'number',
                                label: 'Small Slides Per View',
                                name: 'smallSlidesPerView',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Small Space Between',
                                name: 'smallSpaceBetween',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Small Slider Per Group',
                                name: 'smallSlidesPerGroup',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Small Grid Rows',
                                name: 'smallGridRows',
                                changeProp: 1,
                                min: 1,
                            },

                            // Medium
                            {
                                type: 'number',
                                label: 'Medium Slides Per View',
                                name: 'mediumSlidesPerView',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Medium Space Between',
                                name: 'mediumSpaceBetween',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Medium Slider Per Group',
                                name: 'mediumSlidesPerGroup',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Medium Grid Rows',
                                name: 'mediumGridRows',
                                changeProp: 1,
                                min: 1,
                            },

                            // Large
                            {
                                type: 'number',
                                label: 'Large Slides Per View',
                                name: 'largeSlidesPerView',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Large Space Between',
                                name: 'largeSpaceBetween',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Large Slider Per Group',
                                name: 'largeSlidesPerGroup',
                                changeProp: 1,
                            },
                            {
                                type: 'number',
                                label: 'Large Grid Rows',
                                name: 'largeGridRows',
                                changeProp: 1,
                                min: 1,
                            },
                        ],
                        droppable: `${wrapperSelector},${slideSelector}, ${prevSelector}, ${nextSelector}, ${scrollbarSelector}, ${paginationSelector}}`,
                        draggable: true,
                        classes: ['sio-cms-swiper-slider'],

                        'script-deps': config.script,
                        'style-deps': config.style,
                        'class-container': config.classContainer,
                        'class-wrapper': config.classWrapper,
                        'class-prev': config.classPrev,
                        'class-next': config.classNext,
                        'class-pagination': config.classPagination,
                        'class-scrollbar': config.classScrollbar,
                        script() {
                            const el = this;
                            const scriptDeps = '{[ script-deps ]}';
                            const styleDeps = '{[ style-deps ]}';
                            const falsies = ['0', 'false', 'none'];
                            const truthies = ['1', 'true'];
                            const elId = el.getAttribute('id');

                            const spaceBetween = parseInt(
                                '{[ spaceBetween ]}',
                                10
                            );
                            const smallGridRows = parseInt(
                                '{[ smallGridRows ]}',
                                10
                            );
                            const mediumGridRows = parseInt(
                                '{[ mediumGridRows ]}',
                                10
                            );
                            const largeGridRows = parseInt(
                                '{[ largeGridRows ]}',
                                10
                            );
                            const smallSpaceBetween = parseInt(
                                '{[ smallSpaceBetween ]}',
                                10
                            );
                            const mediumSpaceBetween = parseInt(
                                '{[ mediumSpaceBetween ]}',
                                10
                            );
                            const largeSpaceBetween = parseInt(
                                '{[ largeSpaceBetween ]}',
                                10
                            );

                            const options = {
                                initialSlide: parseInt(
                                    '{[ initialSlide ]}',
                                    10
                                ),
                                speed: parseInt('{[ speed ]}', 10),
                                slidesPerView: parseInt(
                                    '{[ slidesPerView ]}',
                                    10
                                ),
                                spaceBetween: parseInt(
                                    '{[ spaceBetween ]}',
                                    10
                                ),
                                slidesPerGroup: parseInt(
                                    '{[ slidesPerGroup ]}',
                                    10
                                ),
                                slidesOffsetBefore: parseInt(
                                    '{[ slidesOffsetBefore ]}',
                                    10
                                ),
                                slidesOffsetAfter: parseInt(
                                    '{[ slidesOffsetAfter ]}',
                                    10
                                ),
                                direction: '{[ direction ]}',
                                effect: '{[ effect ]}',
                                freeMode: {
                                    enabled: isNaN('{[ freeMode ]}'),
                                },
                                autoHeight: isNaN('{[ autoHeight ]}'),
                                watchOverflow: isNaN('{[ watchOverflow ]}'),
                                centeredSlides: isNaN('{[ centeredSlides ]}'),
                                loop: isNaN('{[ loop ]}'),
                                navigation: {
                                    nextEl: `#${elId} .swiper-button-next`,
                                    prevEl: `#${elId} .swiper-button-prev`,
                                },
                                breakpointsInverse: true,
                                breakpoints: {
                                    576: {
                                        slidesPerView: parseInt(
                                            '{[ smallSlidesPerView ]}',
                                            10
                                        ),
                                        spaceBetween: parseInt(
                                            '{[ smallSpaceBetween ]}',
                                            10
                                        ),
                                        slidesPerGroup: parseInt(
                                            '{[ smallSlidesPerGroup ]}',
                                            10
                                        ),
                                        grid:
                                            smallGridRows > 1
                                                ? {
                                                      rows: smallGridRows,
                                                      fill: 'row',
                                                  }
                                                : undefined,
                                    },
                                    768: {
                                        slidesPerView: parseInt(
                                            '{[ mediumSlidesPerView ]}',
                                            10
                                        ),
                                        spaceBetween: parseInt(
                                            '{[ mediumSpaceBetween ]}',
                                            10
                                        ),
                                        slidesPerGroup: parseInt(
                                            '{[ mediumSlidesPerGroup ]}',
                                            10
                                        ),
                                        grid:
                                            mediumGridRows > 1
                                                ? {
                                                      rows: mediumGridRows,
                                                      fill: 'row',
                                                  }
                                                : undefined,
                                    },
                                    992: {
                                        slidesPerView: parseInt(
                                            '{[ largeSlidesPerView ]}',
                                            10
                                        ),
                                        spaceBetween: parseInt(
                                            '{[ largeSpaceBetween ]}',
                                            10
                                        ),
                                        slidesPerGroup: parseInt(
                                            '{[ largeSlidesPerGroup ]}',
                                            10
                                        ),
                                        grid:
                                            largeGridRows > 1
                                                ? {
                                                      rows: largeGridRows,
                                                      fill: 'row',
                                                  }
                                                : undefined,
                                    },
                                },
                            };

                            if (!falsies.includes('{[ pagination ]}')) {
                                options.pagination = {
                                    el: `#${elId} .swiper-pagination`,
                                    type: '{[ pagination ]}',
                                };
                            }

                            if (truthies.includes('{[ autoplay ]}')) {
                                options.autoplay = {
                                    delay: '{[ autoplayDelay ]}',
                                    stopOnLastSlide:
                                        '{[ autoplayStopOnLastSlide ]}',
                                    disableOnInteraction:
                                        '{[ autoplayDisableOnInteraction ]}',
                                    reverseDirection:
                                        '{[ autoplayReverseDirection ]}',
                                    waitForTransition:
                                        '{[ autoplayWaitForTransition ]}',
                                };
                            } else {
                                options.autoplay = false;
                            }

                            if (isNaN('{[ scrollbar ]}')) {
                                options.scrollbar = {
                                    el: `#${elId} .swiper-scrollbar`,
                                    draggable: true,
                                };
                            }

                            const initSlider = function () {
                                window.sliderSwiper = window.sliderSwiper || {};

                                if (window.sliderSwiper?.[elId]) {
                                    window.sliderSwiper[elId]?.destroy(
                                        true,
                                        true
                                    );
                                }
                                window.sliderSwiper[elId] = new Swiper(
                                    el,
                                    options
                                );

                                const getCurrentGridRows = function () {
                                    if (!window.sliderSwiper?.[elId]) return 1;

                                    const width = window.innerWidth;
                                    if (width >= 992) {
                                        return largeGridRows > 1
                                            ? largeGridRows
                                            : gridRows;
                                    } else if (width >= 768) {
                                        return mediumGridRows > 1
                                            ? mediumGridRows
                                            : gridRows;
                                    } else if (width >= 576) {
                                        return smallGridRows > 1
                                            ? smallGridRows
                                            : gridRows;
                                    }
                                    return gridRows;
                                };

                                const getCurrentSpaceBetween = function () {
                                    if (!window.sliderSwiper?.[elId])
                                        return spaceBetween;

                                    const width = window.innerWidth;
                                    if (width >= 992) {
                                        return largeSpaceBetween;
                                    } else if (width >= 768) {
                                        return mediumSpaceBetween;
                                    } else if (width >= 576) {
                                        return smallSpaceBetween;
                                    }
                                    return spaceBetween;
                                };

                                // Auto-calculate and set height for grid layout
                                const calculateGridHeight = function () {
                                    const currentGridRows = getCurrentGridRows();
                                    if (
                                        currentGridRows > 1 &&
                                        window.sliderSwiper?.[elId]
                                    ) {
                                        const wrapper = el.querySelector(
                                            '.swiper-wrapper'
                                        );
                                        const slides = wrapper
                                            ? wrapper.querySelectorAll(
                                                  '.swiper-slide'
                                              )
                                            : [];
                                        if (slides.length > 0) {
                                            const firstSlide = slides[0];
                                            const slideHeight =
                                                firstSlide.offsetHeight ||
                                                firstSlide.clientHeight;
                                            if (slideHeight > 0) {
                                                const currentSpaceBetween = getCurrentSpaceBetween();
                                                const totalHeight =
                                                    slideHeight *
                                                        currentGridRows +
                                                    currentSpaceBetween *
                                                        (currentGridRows - 1);
                                                el.style.height =
                                                    totalHeight + 'px';
                                                el.style.minHeight =
                                                    totalHeight + 'px';
                                                window.sliderSwiper?.[
                                                    elId
                                                ].update();
                                                return true;
                                            }
                                        }
                                    } else {
                                        // Reset height when grid is disabled
                                        el.style.height = '';
                                        el.style.minHeight = '200px';
                                    }
                                    return false;
                                };

                                if (
                                    smallGridRows > 1 ||
                                    mediumGridRows > 1 ||
                                    largeGridRows > 1
                                ) {
                                    if (!calculateGridHeight()) {
                                        setTimeout(function () {
                                            if (!calculateGridHeight()) {
                                                setTimeout(
                                                    calculateGridHeight,
                                                    200
                                                );
                                            }
                                        }, 50);
                                    }

                                    let resizeTimeout;
                                    window.addEventListener(
                                        'resize',
                                        function () {
                                            clearTimeout(resizeTimeout);
                                            resizeTimeout = setTimeout(
                                                function () {
                                                    calculateGridHeight();
                                                },
                                                150
                                            );
                                        }
                                    );
                                } else {
                                    el.style.height = '';
                                    el.style.minHeight = '200px';
                                }
                            };

                            if (scriptDeps && typeof Swiper == 'undefined') {
                                // Load the style tag
                                var style = document.createElement('link');
                                style.rel = 'stylesheet';
                                style.href = styleDeps;
                                document.head.appendChild(style);

                                // Load the script tag
                                var script = document.createElement('script');
                                script.src = scriptDeps;
                                script.onload = initSlider;
                                document.head.appendChild(script);
                            } else {
                                initSlider();
                            }
                        },
                        ...config.sliderProps,
                    };

                    // Set default properties
                    this.set(defaultProps);

                    // Try to load saved state from data attributes
                    const hasSavedState = loadComponentStateFromAttributes(
                        this
                    );

                    // Add event listeners for trait changes
                    this.on('change', (model, value, opts) => {
                        // Only save when actual traits change, not during initialization
                        if (!opts?.avoidStore && !opts?.initialLoad) {
                            saveComponentStateToAttributes(this);
                        }
                    });

                    // Set up individual trait change listeners for immediate saving
                    this.get('traits').forEach((trait) => {
                        const traitName = trait.get('name');
                        this.on(`change:${traitName}`, (model, value, opts) => {
                            if (!opts.avoidStore && !opts.initialLoad) {
                                console.log(
                                    `Trait ${traitName} changed to ${value}`
                                );

                                // Update the specific data attribute
                                const dataAttrName = `data-trait-${traitName}`;
                                model.addAttributes({ [dataAttrName]: value });
                            }
                        });
                    });
                },
            },
            {
                isComponent(el) {
                    if (el.hasAttribute && el.hasAttribute(containerId)) {
                        return { type: containerName };
                    }
                },
            }
        ),

        view: defaultView.extend({
            init() {
                const props = [
                    'initialSlide',
                    'speed',
                    'slidesPerView',
                    'spaceBetween',
                    'slidesPerGroup',
                    'slidesOffsetBefore',
                    'slidesOffsetAfter',
                    'direction',
                    'effect',
                    'freeMode',
                    'autoHeight',
                    'watchOverflow',
                    'centeredSlides',
                    'loop',
                    'pagination',
                    'scrollbar',
                    'hideNavigation',
                    'autoplay',
                    'autoplayDelay',
                    'autoplayStopOnLastSlide',
                    'autoplayDisableOnInteraction',
                    'autoplayReverseDirection',
                    'autoplayWaitForTransition',
                    // Small
                    'smallSlidesPerView',
                    'smallSpaceBetween',
                    'smallSlidesPerGroup',
                    'smallGridRows',
                    // Medium
                    'mediumSlidesPerView',
                    'mediumSpaceBetween',
                    'mediumSlidesPerGroup',
                    'mediumGridRows',
                    // Large
                    'largeSlidesPerView',
                    'largeSpaceBetween',
                    'largeSlidesPerGroup',
                    'largeGridRows',
                ];

                const reactTo = props.map((prop) => `change:${prop}`).join(' ');
                this.listenTo(this.model, reactTo, this.render);

                const comps = this.model.components();

                // Add a basic template if it's not yet initialized
                if (!comps.length) {
                    comps.add(`
                            <div data-gjs-type="${wrapperName}">${config.slideEls}</div>
                            <div data-gjs-type="${prevName}"></div>
                            <div data-gjs-type="${nextName}"></div>
                            <div data-gjs-type="${paginationName}"></div>
                            <div data-gjs-type="${scrollbarName}"></div>
                          `);
                }
            },
        }),
    });
};
